import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { API, post, errMsg } from "../lib/api";
import { money } from "../lib/products";
import { inputCls } from "../components/site/Field";

const TOKEN_KEY = "danobars_admin_token";
const fmt = (d) => (d ? new Date(d).toLocaleString() : "");

const COLUMNS = {
  orders: [["Date", (r) => fmt(r.created_at)], ["Order", (r) => r.order_number], ["Customer", (r) => `${r.customer?.name ?? ""} · ${r.customer?.email ?? ""}`], ["Items", (r) => (r.items ?? []).map((i) => `${i.name} ${i.variant_label} ×${i.qty}`).join(", ")], ["Ship to", (r) => [r.customer?.address, r.customer?.city, r.customer?.postal_code, r.customer?.country].filter(Boolean).join(", ")], ["Subtotal", (r) => money(r.subtotal)]],
  signups: [["Date", (r) => fmt(r.created_at)], ["Email", (r) => r.email], ["Source", (r) => r.source]],
  contacts: [["Date", (r) => fmt(r.created_at)], ["Name", (r) => r.name], ["Email", (r) => r.email], ["Subject", (r) => r.subject], ["Message", (r) => r.message]],
  wholesale: [["Date", (r) => fmt(r.created_at)], ["Business", (r) => `${r.business_name}${r.business_type ? ` (${r.business_type})` : ""}`], ["Contact", (r) => r.contact_name], ["Email / Phone", (r) => [r.email, r.phone].filter(Boolean).join(" · ")], ["Location", (r) => r.location], ["Volume", (r) => r.estimated_volume], ["Message", (r) => r.message]],
};
const TABS = [["orders", "Orders"], ["signups", "Email sign-ups"], ["contacts", "Contact"], ["wholesale", "Wholesale"]];

const DataTable = ({ rows, cols, id }) =>
  rows.length === 0 ? (
    <p className="py-10 text-center text-dano-ink/70" data-testid={`admin-empty-${id}`}>Nothing yet.</p>
  ) : (
    <div className="overflow-x-auto rounded-2xl border-2 border-dano-ink bg-white/60" data-lenis-prevent>
      <table className="w-full min-w-[720px] text-left text-sm" data-testid={`admin-table-${id}`}>
        <thead className="bg-dano-ink text-dano-cream">
          <tr>{cols.map(([h]) => <th key={h} className="px-4 py-3 font-cond text-sm uppercase tracking-[0.15em]">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-dano-ink/15">
          {rows.map((r) => (
            <tr key={r.id} className="align-top">{cols.map(([h, f]) => <td key={h} className="px-4 py-3 whitespace-pre-wrap">{f(r)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );

const Login = ({ onToken }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await post("/admin/login", { password: pw });
      onToken(data?.token);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-10 max-w-md rounded-[28px] border-2 border-dano-ink p-8">
      <h1 className="font-wide text-3xl uppercase">Admin</h1>
      <p className="mt-2 text-dano-ink/70">Enter the admin password.</p>
      <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="admin-login-password-input" className={`${inputCls} mt-6`} placeholder="Password" />
      {err && <p className="mt-3 text-sm text-dano-red" data-testid="admin-login-error">{err}</p>}
      <button type="submit" disabled={loading} data-testid="admin-login-submit" className="btn-press mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-dano-ink py-4 font-cond text-lg uppercase tracking-widest text-dano-cream hover:bg-dano-red">
        {loading && <Loader2 className="h-5 w-5 animate-spin" />} Log in
      </button>
    </form>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [data, setData] = useState(null);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setData(null);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    sessionStorage.setItem(TOKEN_KEY, token);
    axios
      .get(`${API}/admin/data`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data ?? {}))
      .catch(() => logout());
    return undefined;
  }, [token, logout]);

  return (
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pt-28 sm:pt-32 pb-24" data-testid="admin-page">
      {!token ? (
        <Login onToken={setToken} />
      ) : !data ? (
        <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h1 className="font-wide text-4xl sm:text-5xl uppercase">Dashboard</h1>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 font-cond uppercase tracking-widest hover:text-dano-red"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
          <Tabs defaultValue="orders" className="mt-8">
            <TabsList className="h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
              {TABS.map(([k, label]) => (
                <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`} className="rounded-full border-2 border-dano-ink px-4 py-2 font-cond uppercase tracking-widest data-[state=active]:bg-dano-ink data-[state=active]:text-dano-cream">
                  {label} <span className="ml-2 text-dano-red">{(data?.[k] ?? []).length}</span>
                </TabsTrigger>
              ))}
            </TabsList>
            {TABS.map(([k]) => (
              <TabsContent key={k} value={k} className="mt-6">
                <DataTable id={k} rows={Array.isArray(data?.[k]) ? data[k] : []} cols={COLUMNS[k]} />
              </TabsContent>
            ))}
          </Tabs>
        </>
      )}
    </div>
  );
}
